/* Quick message templates for the admin lead drawer (Phase B will surface them as buttons).
 *
 * Each template is a pure function that takes a lead and returns { subject, body }.
 * The drawer will use these to open mailto: / sms: links — never auto-send.
 *
 * Tone: friendly-professional, written for small-business owners. Signature: "— Mo, Swipetronics"
 * (or "— Swipetronics" when a personal sign-off doesn't fit).
 */

function firstName(lead) {
  const full = (lead?.contact_name || "").trim();
  if (!full) return "there";
  return full.split(/\s+/)[0];
}
function business(lead) {
  return (lead?.business_name || "").trim();
}

export const QUICK_TEMPLATES = [
  {
    id: "request_statement",
    label: "Request Processing Statement",
    channel: "email",
    build: (lead) => ({
      subject: `Quick request — your current processing statement`,
      body:
`Hi ${firstName(lead)},

Thanks for reaching out to Swipetronics. To put together an accurate savings analysis${business(lead) ? ` for ${business(lead)}` : ""}, could you send over a recent merchant processing statement? Even a single page works.

You can reply to this email with it attached, or text it to me directly. We'll review it and send back a clear side-by-side comparison.

— Mo, Swipetronics`,
    }),
  },
  {
    id: "no_answer",
    label: "No-Answer Follow-Up",
    channel: "email",
    build: (lead) => ({
      subject: `Tried reaching you — Swipetronics`,
      body:
`Hi ${firstName(lead)},

Just tried calling${business(lead) ? ` about ${business(lead)}'s processing` : ""} and missed you. No worries — what's a good time to chat? It's a quick call to walk through what we'd save you each month.

You can reply with a time that works or text me back.

— Mo, Swipetronics`,
    }),
  },
  {
    id: "free_pos",
    label: "Free POS Follow-Up",
    channel: "email",
    build: (lead) => ({
      subject: `Your Free POS program details — Swipetronics`,
      body:
`Hi ${firstName(lead)},

Following up on the Free POS program${business(lead) ? ` for ${business(lead)}` : ""}. The hardware is fully covered as long as the merchant account is active — no upfront cost, no hidden lease.

Want to grab 10 minutes this week to confirm fit and get the paperwork started?

— Mo, Swipetronics`,
    }),
  },
  {
    id: "cash_discount",
    label: "Cash Discount Follow-Up",
    channel: "email",
    build: (lead) => ({
      subject: `Cash Discount program — next steps`,
      body:
`Hi ${firstName(lead)},

Following up on the Cash Discount program${business(lead) ? ` for ${business(lead)}` : ""}. Most merchants we set up are eliminating 90%+ of their monthly processing fees in the first cycle.

If you can send a recent statement, I'll send back exact numbers within 24 hours.

— Mo, Swipetronics`,
    }),
  },
  {
    id: "proposal_sent",
    label: "Proposal Sent Follow-Up",
    channel: "email",
    build: (lead) => ({
      subject: `Following up on your Swipetronics proposal`,
      body:
`Hi ${firstName(lead)},

Checking in on the proposal we sent over${business(lead) ? ` for ${business(lead)}` : ""}. Happy to walk through any line item or adjust anything if it'd help.

Just reply with questions or a good time to chat.

— Mo, Swipetronics`,
    }),
  },
  {
    id: "appointment_confirm",
    label: "Appointment Confirmation",
    channel: "email",
    build: (lead) => ({
      subject: `Confirming our call — Swipetronics`,
      body:
`Hi ${firstName(lead)},

Confirming our scheduled call. I'll have a quick savings breakdown ready${business(lead) ? ` for ${business(lead)}` : ""} so we can make the conversation productive.

If anything changes, just reply here or text me.

— Mo, Swipetronics`,
    }),
  },
];

/* Build a mailto: URL from a template + lead. */
export function buildMailtoUrl(template, lead) {
  const { subject, body } = template.build(lead);
  const to = encodeURIComponent(lead?.email || "");
  const s = encodeURIComponent(subject);
  const b = encodeURIComponent(body);
  return `mailto:${to}?subject=${s}&body=${b}`;
}

/* Build an sms: URL — most templates are email-shaped, so SMS uses the body only,
 * trimmed to ~280 chars to be safe across carriers. */
export function buildSmsUrl(template, lead) {
  const { body } = template.build(lead);
  const phone = (lead?.phone || "").replace(/[^\d+]/g, "");
  const trimmed = body.length > 280 ? body.slice(0, 277) + "…" : body;
  return `sms:${phone}?&body=${encodeURIComponent(trimmed)}`;
}
